/**
 * PlaintextConfig types + local cache for the parent PWA config editor.
 *
 * The Rust source of truth is `backstop_common::config::PlaintextConfig`
 * (crates/backstop-common/src/config.rs) and `HarmsTaxonomy`
 * (crates/backstop-common/src/taxonomy.rs). Field names here must match the
 * `serde` names on those structs so round-trip through JSON is byte-stable
 * with the endpoint decrypt path in `backstop_core::e2ee_ops::decrypt_config`.
 *
 * The version-derived AAD mirrors that same function:
 *   aad = u64::to_be_bytes(version)   // 8 bytes, big-endian
 */

import { signal } from '@preact/signals';
import { encrypt, decrypt } from '../e2ee/envelope';
import { familyKeyInMemory } from '../state/session';
import { api } from '../state/api';
import { standard } from '../config/presets';
import * as vault from '../e2ee/vault';

// -----------------------------------------------------------------------------
// Types — mirror of Rust PlaintextConfig
// -----------------------------------------------------------------------------

export interface HarmCategory {
  id: string;
  label: string;
  /**
   * Prose block fed verbatim into the LLM prompt. Should describe both what
   * to flag AND what NOT to flag — the "enough is enough" line lives in
   * this text, not in a separate threshold field.
   */
  guidance: string;
}

export interface HarmsTaxonomy {
  version: number;
  categories: HarmCategory[];
}

/**
 * `BlocklistEntry` in Rust is `#[serde(untagged)]`, so each entry is either
 * `{ domain: string }` or `{ url_pattern: string }`. Modeling as a union of
 * exclusive shapes keeps the JSON compatible without needing a discriminator.
 */
export type BlocklistEntry = { domain: string } | { url_pattern: string };

export interface Blocklist {
  id: string;
  label: string;
  entries: BlocklistEntry[];
}

export interface Cadence {
  active_seconds: number;
  idle_seconds: number;
}

export interface ByokProvider {
  provider: string;
  model: string;
  /** null / omitted for non-custom providers. */
  endpoint: string | null;
  /**
   * Append `/chat/completions` to `endpoint`? Absent = `true` (append) —
   * matches the Rust `#[serde(default = "default_true")]`.
   */
  endpoint_append_path?: boolean;
}

/**
 * A single active-capture window in local time. Times are 24h "HH:MM:SS" or
 * "HH:MM" — chrono's NaiveTime accepts either on the Rust side; the PWA
 * always serialises "HH:MM:SS" via the `<input type="time">` value plus a
 * ":00" seconds suffix (see ScheduleEditor).
 */
export interface TimeWindow {
  from: string;
  to: string;
}

/**
 * Per-day-of-week list of active capture windows. Empty on every day means
 * "fall back to `active_hours`" — the endpoint only enforces this as an
 * off-hours gate when at least one day has a configured window.
 */
export interface WeeklySchedule {
  monday: TimeWindow[];
  tuesday: TimeWindow[];
  wednesday: TimeWindow[];
  thursday: TimeWindow[];
  friday: TimeWindow[];
  saturday: TimeWindow[];
  sunday: TimeWindow[];
}

export const WEEKDAYS: readonly (keyof WeeklySchedule)[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export function emptyWeeklySchedule(): WeeklySchedule {
  return {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  };
}

export interface PlaintextConfig {
  taxonomy: HarmsTaxonomy;
  blocklists: Blocklist[];
  exceptions: string[];
  cadence: Cadence;
  /** Rust tuple `(String, String)` — 24h "HH:MM" pair. */
  active_hours: [string, string];
  retention_days: number;
  /**
   * Master kill switch. `false` pauses all capture on the endpoint and logs
   * a `paused` activity entry per tick so the dashboard reflects the choice.
   * Absent from old cached configs — Rust defaults to `true` on the way in;
   * the PWA defaults it here so UI never has to handle undefined.
   */
  collection_enabled: boolean;
  /**
   * Per-day-of-week active windows in the endpoint's local time. Empty on
   * every day means "fall back to `active_hours`" for the off-hours gate.
   */
  schedule: WeeklySchedule;
  /**
   * Per-device overrides, keyed by endpoint id. Authoring state: only this
   * app ever reads it.
   *
   * It rides in the family config because that is the one place overrides
   * are already durable, encrypted, and synced across the parent's
   * browsers — IndexedDB alone would lose them on a browser reinstall or a
   * second parent device.
   *
   * `resolve()` strips it from the per-device documents we write, so no
   * endpoint ever receives its siblings' settings.
   */
  device_overrides: Record<string, DeviceOverride>;
}

/**
 * The sections of a config a single device may override.
 *
 * Granularity is the whole section, never the field. "Override category 3's
 * guidance but inherit the other four" can't be rendered honestly — a parent
 * could no longer tell at a glance what a device is running — and
 * section-level is what keeps `resolve()` a one-line spread.
 *
 * `byok` is absent by design: provider, model, and API key are one per
 * family (the key is a single row in `family_byok_secrets`).
 *
 * Mirrors `backstop_common::config::DeviceOverride`.
 */
export type DeviceOverride = Partial<
  Pick<
    PlaintextConfig,
    | 'taxonomy'
    | 'blocklists'
    | 'exceptions'
    | 'cadence'
    | 'active_hours'
    | 'schedule'
    | 'collection_enabled'
    | 'retention_days'
  >
>;

/** Every section a device may override, in the order the UI presents them. */
export const OVERRIDABLE_SECTIONS = [
  'collection_enabled',
  'schedule',
  'cadence',
  'taxonomy',
  'blocklists',
  'retention_days',
] as const satisfies readonly (keyof DeviceOverride)[];

/**
 * Family defaults + one device's overrides → the complete document that
 * device runs.
 *
 * A shallow spread is the entire merge, which is the whole argument for
 * section-level granularity: there is no per-field reconciliation to get
 * wrong, and what the endpoint receives is exactly what the parent sees on
 * the device page.
 *
 * `device_overrides` is dropped rather than copied. It is family-wide
 * authoring state; shipping it to each endpoint would hand one compromised
 * laptop every other device's settings for no benefit — the endpoint has
 * already been given its answer.
 */
export function resolve(
  base: PlaintextConfig,
  override: DeviceOverride | undefined,
): PlaintextConfig {
  const { device_overrides: _authoringState, ...rest } = base;
  return { ...rest, ...(override ?? {}), device_overrides: {} };
}

/** How many sections this device has customised. Drives the dashboard badge. */
export function overrideCount(override: DeviceOverride | undefined): number {
  if (override === undefined) return 0;
  return OVERRIDABLE_SECTIONS.filter((k) => override[k] !== undefined).length;
}

// -----------------------------------------------------------------------------
// Defaults + AAD helper
// -----------------------------------------------------------------------------

/** A minimal safe default config, used to seed the editor before first save. */
export const DEFAULT_CONFIG: PlaintextConfig = {
  taxonomy: standard,
  blocklists: [],
  exceptions: [],
  cadence: { active_seconds: 600, idle_seconds: 3600 },
  active_hours: ['07:00', '23:00'],
  retention_days: 7,
  collection_enabled: true,
  // Sensible starting schedule: 07:30–22:00 every day. The parent can
  // trim, split, or add windows in ScheduleEditor. Any day left non-empty
  // means the schedule is authoritative on the endpoint; clearing every
  // day falls back to the legacy `active_hours` window.
  schedule: {
    monday: [{ from: '07:30:00', to: '22:00:00' }],
    tuesday: [{ from: '07:30:00', to: '22:00:00' }],
    wednesday: [{ from: '07:30:00', to: '22:00:00' }],
    thursday: [{ from: '07:30:00', to: '22:00:00' }],
    friday: [{ from: '07:30:00', to: '22:00:00' }],
    saturday: [{ from: '07:30:00', to: '22:00:00' }],
    sunday: [{ from: '07:30:00', to: '22:00:00' }],
  },
  device_overrides: {},
};

/**
 * Fixed AAD for family-config envelopes.
 *
 * Was previously derived from the version number (`u64::to_be_bytes`). That
 * broke the moment we switched to server-driven versioning: the PWA encrypts
 * before the server assigns a version, so it couldn't know what AAD to
 * bind. Fixed-string AAD is version-independent — the server's monotonic
 * `version_number` column is what prevents replay now.
 *
 * Endpoint side in `crates/backstop-core/src/e2ee_ops.rs` uses the same
 * literal, byte-for-byte.
 */
export const CONFIG_AAD: Uint8Array = new TextEncoder().encode(
  'family-config:v1',
);

/**
 * Historical shim: some call sites still take a `version` parameter for API
 * compatibility with the pre-fixed-AAD flow. Ignored — returns the fixed
 * AAD regardless. Delete once every caller drops the argument.
 */
export function versionToAad(_version: number): Uint8Array {
  return CONFIG_AAD;
}

// -----------------------------------------------------------------------------
// Local plaintext cache (IndexedDB via `vault`)
// -----------------------------------------------------------------------------

const CACHE_KEY = 'config_plaintext_v1';

interface CachedConfig {
  version: number;
  config: PlaintextConfig;
}

/** Load the last-known plaintext config + version, or null if never saved. */
export async function loadCachedConfig(): Promise<CachedConfig | null> {
  const raw = await vault.get(CACHE_KEY);
  if (raw === null) return null;
  const json = new TextDecoder().decode(raw);
  return JSON.parse(json) as CachedConfig;
}

/** Persist `config` + `version` locally so subsequent edits skip refetch. */
export async function saveCachedConfig(
  config: PlaintextConfig,
  version: number,
): Promise<void> {
  const payload: CachedConfig = { version, config };
  const bytes = new TextEncoder().encode(JSON.stringify(payload));
  await vault.put(CACHE_KEY, bytes);
}

/** Remove the cached config (used in tests + on logout). */
export async function clearCachedConfig(): Promise<void> {
  await vault.del(CACHE_KEY);
}

// -----------------------------------------------------------------------------
// Reactive state + control-plane sync
// -----------------------------------------------------------------------------

export const currentConfig = signal<PlaintextConfig | null>(null);
export const currentVersion = signal<number>(0);

/** Base64-encode a byte array (browser-compatible btoa). */
export function toBase64(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

/**
 * Fill in fields absent from a config written by an older build.
 *
 * Rust supplies `#[serde(default)]` on the way in; TypeScript has no
 * equivalent, so a config saved before `device_overrides` existed would
 * arrive with the field `undefined` and every `cfg.device_overrides[id]`
 * read would throw. Normalise once, at each parse boundary, so the rest of
 * the app can treat the field as always present.
 */
function normalize(cfg: PlaintextConfig): PlaintextConfig {
  return { ...cfg, device_overrides: cfg.device_overrides ?? {} };
}

/** Base64-decode into bytes. */
function fromBase64(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

interface LatestConfigResp {
  version: number;
  /** Base64-encoded ciphertext bytes (matches `serde_bytes` on the Rust side). */
  content_ct: string;
}

/**
 * Seed `currentConfig` + `currentVersion` from (in order):
 *   1. IndexedDB cache
 *   2. Control-plane `/v1/family/config/latest` (decrypt)
 *   3. `DEFAULT_CONFIG` (version 0)
 *
 * Any network / decrypt error falls through to the next tier. This keeps the
 * editor usable offline and before the parent has ever saved.
 */
export async function fetchLatest(): Promise<void> {
  const cached = await loadCachedConfig();
  if (cached !== null) {
    currentConfig.value = normalize(cached.config);
    currentVersion.value = cached.version;
    return;
  }

  try {
    const raw = await api<LatestConfigResp>('/v1/family/config/latest');
    const fk = familyKeyInMemory.value;
    if (fk === null) throw new Error('no family key in memory');
    const ct = fromBase64(raw.content_ct);
    const aad = versionToAad(raw.version);
    const pt = await decrypt(fk, ct, aad);
    const cfg = normalize(
      JSON.parse(new TextDecoder().decode(pt)) as PlaintextConfig,
    );
    currentConfig.value = cfg;
    currentVersion.value = raw.version;
    await saveCachedConfig(cfg, raw.version);
    return;
  } catch {
    // No config on server yet, or auth/decrypt failed — seed the default.
    currentConfig.value = DEFAULT_CONFIG;
    currentVersion.value = 0;
  }
}

/**
 * Force a refresh from the server, ignoring the IndexedDB cache. Used by
 * `save()` after a 409 conflict — the cache is behind reality, so the only
 * way to compute the next legal version is to re-read the server. Preserves
 * the same fall-through semantics on error as `fetchLatest`.
 */
export async function refreshLatestFromServer(): Promise<void> {
  const raw = await api<LatestConfigResp>('/v1/family/config/latest').catch(
    (e) => {
      console.warn('[config] /latest fetch failed:', e);
      return null;
    },
  );
  // The server responds 204 when there is no config yet; api<T> returns
  // null in that case (see pwa/src/state/api.ts). Treat as "seed default".
  if (raw === null) {
    currentConfig.value = DEFAULT_CONFIG;
    currentVersion.value = 0;
    return;
  }
  // Adopt the server's version FIRST — even if we later fail to decrypt.
  // Historically the catch here was suppressing everything, which kept
  // `currentVersion` at 0 and forced every save to try `version=1`, hitting
  // a 409 loop when the server was at 1+. If the stored ciphertext is bad
  // (legacy wire-format bug), we still want the next save to use
  // `serverVersion + 1` so it overwrites the corrupt row cleanly.
  currentVersion.value = raw.version;
  try {
    const fk = familyKeyInMemory.value;
    if (fk === null) throw new Error('no family key in memory');
    const ct = fromBase64(raw.content_ct);
    const aad = versionToAad(raw.version);
    const pt = await decrypt(fk, ct, aad);
    const cfg = normalize(
      JSON.parse(new TextDecoder().decode(pt)) as PlaintextConfig,
    );
    currentConfig.value = cfg;
    await saveCachedConfig(cfg, raw.version);
  } catch (e) {
    // Decrypt failed — server has a version but we can't recover the
    // plaintext. Keep the version, seed the editor with defaults so the
    // user can save a fresh config as `serverVersion + 1` and clobber the
    // bad row. Log for visibility.
    console.warn(
      'refreshLatestFromServer: decrypt failed, keeping server version so next save overwrites',
      e,
    );
    if (currentConfig.value === null) currentConfig.value = DEFAULT_CONFIG;
  }
}

/**
 * Encrypt + PUT `next` as the next config version, then persist locally.
 *
 * On 409 (version conflict — another writer beat us OR our local cache is
 * behind reality), we force-refresh from the server, apply the incoming
 * config as the new base, and retry ONCE with `serverVersion + 1`. Retrying
 * more than once would loop forever if a concurrent writer keeps racing us;
 * one retry handles the "cache stale after cross-session PUT" case that
 * covers ~all real-world 409s in a single-parent family.
 */
export async function save(next: PlaintextConfig): Promise<void> {
  const fk = familyKeyInMemory.value;
  if (fk === null) throw new Error('no family key in memory');
  // v1: single-row config per family. Always write version=1; the server
  // UPSERTs on (family_id, version_number) so re-saves overwrite in place.
  // No 409, no refresh, no retry.
  await putConfigAt(1, next, fk);
  currentVersion.value = 1;
}

async function putConfigAt(
  version: number,
  next: PlaintextConfig,
  fk: Uint8Array,
): Promise<void> {
  const aad = versionToAad(version);
  const ct = await encrypt(
    fk,
    new TextEncoder().encode(JSON.stringify(next)),
    aad,
  );
  await api('/v1/family/config', {
    method: 'PUT',
    body: JSON.stringify({ version, content_ct: toBase64(ct) }),
  });
  currentConfig.value = next;
  currentVersion.value = version;
  await saveCachedConfig(next, version);
}
